import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../sessionSlice";
import { apiUrl } from "../../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../../utils/themeAssets";
import "./talentUploads.css";

const PAGE_SIZE = 10;

function matchesSearch(row, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const name = String(row.name || "").toLowerCase();
  const email = String(row.email || "").toLowerCase();
  const employeeId = String(row.employeeId || "").toLowerCase();
  const category = String(row.category || "").toLowerCase();
  const status = row.visibleInGallery ? "visible" : "hidden";
  const points = String(row.points ?? "");
  const likes = String(row.likeCount ?? "");
  const votes = String(row.voteCount ?? "");

  return (
    name.includes(q) ||
    email.includes(q) ||
    employeeId.includes(q) ||
    category.includes(q) ||
    status.includes(q) ||
    points.includes(q) ||
    likes.includes(q) ||
    votes.includes(q)
  );
}

export default function TalentUploads() {
  const adminToken = useSelector(selectAdminToken);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);

  const loadUploads = useCallback(async () => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/adminYogaUploads"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({}),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setUploads(Array.isArray(payload.uploads) ? payload.uploads : []);
      } else {
        setUploads([]);
      }
    } catch (e) {
      console.error("adminYogaUploads", e);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredUploads = useMemo(
    () => uploads.filter((row) => matchesSearch(row, search)),
    [uploads, search]
  );

  const totalPages = Math.max(1, Math.ceil(filteredUploads.length / PAGE_SIZE));

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredUploads.slice(start, start + PAGE_SIZE);
  }, [filteredUploads, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [preview]);

  const downloadCsv = () => {
    const headers = [
      "Player",
      "Email",
      "Employee ID",
      "Category",
      "Media URL",
      "Votes",
      "Heart",
      "Smile",
      "Clap",
      "Jury points",
      "Played mosaic",
      "Upload points",
      "Gallery",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredUploads.map((r) =>
      [
        r.name || "—",
        r.email || "—",
        r.employeeId || "—",
        r.category || "—",
        (r.files || [])
          .map((f) => s3ThemeAssetUrl(f.filename) || f.filename || "")
          .filter(Boolean)
          .join(" | "),
        r.voteCount ?? 0,
        r.heartCount ?? 0,
        r.smileCount ?? 0,
        r.clapCount ?? 0,
        r.juryPoints ?? 0,
        r.playedMosaic ? "Yes" : "No",
        r.points,
        r.visibleInGallery ? "Visible" : "Hidden",
      ]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `uploads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleVisibility = async (row) => {
    if (!adminToken || togglingId) return;
    const nextStatus = row.visibleInGallery ? 0 : 1;
    setTogglingId(row.id);
    try {
      const resp = await fetch(apiUrl("/adminYogaUploadVisibility"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ gameId: row.id, uploadStatus: nextStatus }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Update failed");
      }
      setUploads((prev) =>
        prev.map((u) =>
          u.id === row.id
            ? { ...u, uploadStatus: nextStatus, visibleInGallery: nextStatus === 1 }
            : u
        )
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Could not update visibility.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const deleteUpload = async (row) => {
    if (!adminToken || deletingId) return;
    const result = await Swal.fire({
      title: "Delete this post?",
      text: "Permanently removes the upload and its votes/reactions. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    setDeletingId(row.id);
    try {
      const resp = await fetch(apiUrl("/talentDelete"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ gameId: row.id, adminDelete: true }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Delete failed");
      }
      setUploads((prev) => prev.filter((u) => u.id !== row.id));
    } catch (e) {
      Swal.fire("Error", e.message || "Could not delete upload.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="yau-page" data-tour="admin-uploads">
      <h1 className="yau-title">Gallery uploads</h1>
      <p className="yau-sub">
        Review player submissions, hide or show entries in the public gallery, or permanently delete
        posts. Download CSV includes email, category, votes, reactions, jury points, and mosaic play.
      </p>

      {!loading && uploads.length > 0 ? (
        <div className="yau-toolbar">
          <input
            type="search"
            className="yau-search"
            placeholder="Search by name, email, category, points…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search uploads"
          />
          <span className="yau-count">
            {filteredUploads.length} of {uploads.length} upload{uploads.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            className="yau-download-btn"
            onClick={downloadCsv}
            disabled={filteredUploads.length === 0}
          >
            <i className="fa-solid fa-download" /> Download CSV
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="load-theme-container">
          <div className="load-theme-spinner" />
        </div>
      ) : uploads.length === 0 ? (
        <p className="yau-empty">No player uploads yet for this session.</p>
      ) : filteredUploads.length === 0 ? (
        <p className="yau-empty">No uploads match your search.</p>
      ) : (
        <>
          <div className="yau-table-wrap">
            <table className="yau-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Category</th>
                  <th>Files</th>
                  <th>Votes</th>
                  <th>Gallery</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((row) => (
                  <tr key={row.id} className={row.visibleInGallery ? "" : "yau-row--hidden"}>
                    <td>
                      <div className="yau-player">{row.name || row.email || "—"}</div>
                      {row.email ? <div className="yau-meta">{row.email}</div> : null}
                      {row.employeeId ? (
                        <div className="yau-meta">ID: {row.employeeId}</div>
                      ) : null}
                    </td>
                    <td>{row.category || "—"}</td>
                    <td>
                      <div className="yau-files">
                        {(row.files || []).map((f) => (
                          <button
                            key={f.filename}
                            type="button"
                            className="yau-file-link"
                            onClick={() =>
                              setPreview({
                                filename: f.filename,
                                mediaType: f.mediaType,
                                name: row.name || row.email || "Upload",
                              })
                            }
                          >
                            {f.mediaType === "video" ? "▶ Video" : "🖼 Image"}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td>{row.voteCount ?? 0}</td>
                    <td>
                      <span
                        className={`yau-badge${row.visibleInGallery ? " yau-badge--on" : " yau-badge--off"}`}
                      >
                        {row.visibleInGallery ? "Visible" : "Hidden"}
                      </span>
                    </td>
                    <td>
                      <div className="yau-actions">
                        <button
                          type="button"
                          className="yau-toggle-btn"
                          disabled={togglingId === row.id || deletingId === row.id}
                          onClick={() => toggleVisibility(row)}
                        >
                          {row.visibleInGallery ? "Hide" : "Show"}
                        </button>
                        <button
                          type="button"
                          className="yau-delete-btn"
                          disabled={deletingId === row.id || togglingId === row.id}
                          onClick={() => deleteUpload(row)}
                        >
                          {deletingId === row.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <nav className="yau-pagination" aria-label="Upload pages">
              <button
                type="button"
                className="yau-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="yau-page-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="yau-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}

      {preview ? (
        <div
          className="yau-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Upload preview"
          onClick={() => setPreview(null)}
        >
          <div className="yau-lightbox-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="yau-lightbox-close"
              onClick={() => setPreview(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="yau-lightbox-media">
              {preview.mediaType === "video" ? (
                <video
                  src={s3ThemeAssetUrl(preview.filename)}
                  controls
                  playsInline
                  autoPlay
                />
              ) : (
                <img
                  src={s3ThemeAssetUrl(preview.filename)}
                  alt={preview.name || "Upload"}
                  referrerPolicy="no-referrer"
                />
              )}
            </div>
            {preview.name ? <p className="yau-lightbox-name">{preview.name}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
